import { useMemo, useState } from 'react';
import { isAdminRole, STATUS_COLORS, STATUS_LABELS_GR } from '@/features/requests/requests.helpers';
import { useMarkAsReadMutation } from '@/features/requests/requests.mutations';
import { useRequestCategories, useRequests } from '@/features/requests/requests.queries';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { formatDistanceToNow } from 'date-fns';
import { el } from 'date-fns/locale';
import { Inbox, Plus, Search, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { RequestListDto, RequestStatus } from '@/types/requests.types';
import { cn } from '@/lib/utils';
import useAuth from '@/hooks/use-auth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import RequestCreateSheet from '@/components/sheets/request-sheet/request-create-sheet';
import RequestViewSheet from '@/components/sheets/request-sheet/request-view-sheet';

const requestsSearchSchema = z.object({
  mode: z.enum(['create', 'view']).optional(),
  request: z.number().optional(),
});

export const Route = createFileRoute('/_app/(requests)/requests/')({
  validateSearch: zodSearchValidator(requestsSearchSchema),
  component: RequestListPage,
});

type StatusFilter = 'ALL' | RequestStatus;

function RequestListPage() {
  const { t } = useTranslation(['requests']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [openedRequestIds, setOpenedRequestIds] = useState<number[]>([]);
  const pageSize = 20;

  const params = useMemo(
    () => ({
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      categoryId: categoryFilter !== 'all' ? Number(categoryFilter) : undefined,
      search: search || undefined,
      page,
      pageSize,
    }),
    [statusFilter, categoryFilter, search, page]
  );

  const { data, isLoading } = useRequests(params);
  const { data: categories = [] } = useRequestCategories();
  const { mutate: markAsRead } = useMarkAsReadMutation();

  const requests = useMemo(
    () =>
      (data?.items ?? []).map((request) => ({
        ...request,
        isUnread: request.isUnread && !openedRequestIds.includes(request.id),
      })),
    [data?.items, openedRequestIds]
  );
  const hasMore = data?.hasMore ?? false;

  const handleOpenRequest = (requestId: number, isUnread: boolean) => {
    setOpenedRequestIds((current) =>
      current.includes(requestId) ? current : [...current, requestId]
    );
    if (isUnread) {
      markAsRead(requestId);
    }
    navigate({ to: '.', search: { mode: 'view', request: requestId } });
  };

  const pageTitle = isAdmin ? t('page_title') : t('page_title_mine');

  return (
    <div className='flex h-full flex-col gap-y-3 px-4 py-2'>
      {/* Header */}
      <div className='flex flex-col gap-4 py-4 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-col'>
          <span className='text-xl font-bold'>{pageTitle}</span>
          <span className='text-base font-light text-neutral-500'>{t('page_sub_title')}</span>
        </div>
        <div className='flex gap-2'>
          {isAdmin && (
            <Button
              variant='outline'
              size='icon'
              onClick={() => navigate({ to: '/requests/settings' })}
            >
              <Settings className='h-5 w-5' />
            </Button>
          )}
          <Button
            className='flex items-center gap-2'
            onClick={() => navigate({ to: '.', search: { mode: 'create' } })}
          >
            <Plus strokeWidth={3} className='h-5 w-5' />
            {t('new_request')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
        {/* Status filter chips */}
        <div className='flex flex-wrap gap-2'>
          {(
            [
              { key: 'ALL', label: t('filter_all') },
              { key: 'OPEN', label: t('filter_open') },
              { key: 'IN_PROGRESS', label: t('filter_in_progress') },
              { key: 'CLOSED', label: t('filter_closed') },
            ] as { key: StatusFilter; label: string }[]
          ).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => {
                setStatusFilter(key);
                setPage(1);
              }}
              className={cn(
                'rounded-full border px-3 py-1 text-sm transition-colors',
                statusFilter === key
                  ? 'border-primary bg-primary text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Category filter (admin only) */}
        {isAdmin && categories.length > 0 && (
          <Select
            value={categoryFilter}
            onValueChange={(v) => {
              setCategoryFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className='w-full sm:w-[200px]'>
              <SelectValue placeholder={t('category')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>{t('filter_all')}</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.value} value={String(cat.value)}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Search */}
        <div className='relative flex-1 sm:max-w-xs'>
          <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
          <Input
            className='pl-9'
            placeholder={t('search_placeholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      {/* Request List */}
      <div className='flex flex-1 flex-col gap-3 overflow-y-auto pb-4'>
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className='h-28 w-full rounded-lg' />
          ))
        ) : requests.length === 0 ? (
          <div className='flex flex-1 flex-col items-center justify-center gap-3 py-16 text-gray-400'>
            <Inbox className='h-12 w-12' />
            <span className='text-lg'>{t('no_requests')}</span>
          </div>
        ) : (
          <>
            {requests.map((request) => (
              <RequestCard
                key={request.id}
                request={request}
                isAdmin={isAdmin}
                onOpen={handleOpenRequest}
              />
            ))}
            {hasMore && (
              <Button variant='outline' className='mx-auto' onClick={() => setPage((p) => p + 1)}>
                {t('load_more')}
              </Button>
            )}
          </>
        )}
      </div>

      {/* Sheets */}
      <RequestCreateSheet />
      <RequestViewSheet />
    </div>
  );
}

function RequestCard({
  request,
  isAdmin,
  onOpen,
}: {
  request: RequestListDto;
  isAdmin: boolean;
  onOpen: (requestId: number, isUnread: boolean) => void;
}) {
  const studentsText = request.relatedStudents.map((s) => s.fullName).join(', ');
  const lastMessageSummary = request.lastMessagePreview?.trim();

  return (
    <div
      onClick={() => onOpen(request.id, request.isUnread)}
      className={cn(
        'group flex cursor-pointer flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 transition-all hover:border-gray-300 hover:shadow-sm',
        request.isUnread && 'border-l-4 border-l-blue-500 bg-blue-50/30'
      )}
    >
      <div className='flex items-start justify-between gap-2'>
        <div className='flex flex-wrap items-center gap-2'>
          <Badge className={cn('text-xs', STATUS_COLORS[request.status])}>
            {STATUS_LABELS_GR[request.status]}
          </Badge>
          {request.isUnread && (
            <span className='inline-block h-2 w-2 rounded-full bg-blue-500' aria-hidden='true' />
          )}
          <span className='text-xs text-gray-400'>#{request.id}</span>
        </div>
        <span className='shrink-0 text-xs text-gray-400'>
          {formatDistanceToNow(new Date(request.lastMessageAt || request.updatedAt), {
            addSuffix: true,
            locale: el,
          })}
        </span>
      </div>

      <div className='flex flex-col gap-2'>
        <span
          className={cn(
            'line-clamp-1 text-sm text-gray-900',
            request.isUnread ? 'font-semibold' : 'font-medium'
          )}
        >
          {request.subject}
        </span>
        <div className='flex flex-wrap gap-2 text-xs text-gray-600'>
          <span className='rounded-full bg-gray-100 px-2.5 py-1 font-medium text-gray-700'>
            {request.categoryName}
          </span>
          {studentsText && (
            <span className='rounded-full bg-gray-100 px-2.5 py-1'>
              <span className='font-medium text-gray-700'>Μαθητής:</span> {studentsText}
            </span>
          )}
          {isAdmin && (
            <>
              <span className='rounded-full bg-gray-100 px-2.5 py-1'>
                <span className='font-medium text-gray-700'>Από:</span> {request.creatorName}
              </span>
              {request.recipientName && (
                <span className='rounded-full bg-gray-100 px-2.5 py-1'>
                  <span className='font-medium text-gray-700'>Προς:</span> {request.recipientName}
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {(lastMessageSummary || request.lastMessageFrom) && (
        <div className='rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600 transition-colors group-hover:bg-gray-100/80'>
          {request.lastMessageFrom && (
            <span className='font-medium text-gray-700'>{request.lastMessageFrom}</span>
          )}
          {request.lastMessageFrom && lastMessageSummary && (
            <span className='text-gray-400'> · </span>
          )}
          {lastMessageSummary && <span className='line-clamp-2 block'>{lastMessageSummary}</span>}
        </div>
      )}
    </div>
  );
}
